import {
	CodeBlock,
	Alert,
	Badge,
	Table,
	Card,
	CardGrid,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function Environments() {
	const comparisonData = [
		{
			feature: 'API Base URL',
			sandbox: 'https://api.rngfort.com',
			live: 'https://api.rngfort.com',
		},
		{
			feature: 'Game Client URL',
			sandbox: 'https://play.rngfort.com',
			live: 'https://play.rngfort.com',
		},
		{
			feature: 'Key Prefix (Public)',
			sandbox: <code>pk_test_*</code>,
			live: <code>pk_live_*</code>,
		},
		{
			feature: 'Key Prefix (Secret)',
			sandbox: <code>sk_test_*</code>,
			live: <code>sk_live_*</code>,
		},
		{
			feature: 'Player Balance',
			sandbox: 'Virtual (unlimited)',
			live: 'Real funds',
		},
		{
			feature: 'Wallet Callbacks',
			sandbox: 'Simulated',
			live: 'Real transactions',
		},
		{
			feature: 'Rate Limits',
			sandbox: 'Relaxed',
			live: 'Production limits',
		},
		{
			feature: 'Data Retention',
			sandbox: '30 days',
			live: 'Permanent',
		},
		{
			feature: 'Support SLA',
			sandbox: 'Best effort',
			live: 'Guaranteed response times',
		},
	];

	const comparisonColumns = [
		{ key: 'feature', header: 'Feature', width: '200px' },
		{
			key: 'sandbox',
			header: 'Sandbox',
			render: (value: unknown) => (
				<span style={{ color: '#7c3aed' }}>{value as React.ReactNode}</span>
			),
		},
		{
			key: 'live',
			header: 'Live',
			render: (value: unknown) => (
				<span style={{ color: '#059669' }}>{value as React.ReactNode}</span>
			),
		},
	];

	return (
		<div className={styles.docsPage}>
			<h1>Environments</h1>
			<p className={styles.lead}>
				Ringfort provides two separate environments to support your development
				workflow. Use sandbox for development and testing, then switch to live
				when you're ready to process real transactions.
			</p>

			<h2>Sandbox Environment</h2>

			<div className={`${styles.envCard} ${styles.sandbox}`}>
				<div className={styles.envCardHeader}>
					<h3 className={styles.envCardTitle}>Sandbox</h3>
					<Badge variant="sandbox">Development & Testing</Badge>
				</div>
				<p className={styles.envCardDescription}>
					The sandbox environment is designed for development and testing. All
					transactions are simulated, and players have access to unlimited
					virtual balance.
				</p>
			</div>

			<h3>Key Characteristics</h3>

			<ul>
				<li>
					<strong>Unlimited virtual balance</strong> - Players start with 10,000
					units and can reset their balance at any time
				</li>
				<li>
					<strong>No real money</strong> - All bets and payouts use virtual
					currency with no financial impact
				</li>
				<li>
					<strong>Simulated wallet callbacks</strong> - Webhook endpoints are
					called but no actual transactions occur
				</li>
				<li>
					<strong>Relaxed rate limits</strong> - Higher request limits to
					facilitate testing
				</li>
				<li>
					<strong>Verbose error messages</strong> - Detailed error information
					to help debug integration issues
				</li>
				<li>
					<strong>30-day data retention</strong> - Test data is automatically
					cleaned up after 30 days
				</li>
			</ul>

			<h3>Demo Credentials</h3>

			<p>
				For quick testing, you can use the shared demo credentials. These work
				immediately without any setup:
			</p>

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(2, 1fr)',
					gap: 'var(--space-4)',
					margin: 'var(--space-4) 0',
				}}
			>
				<div className={styles.envDetail}>
					<span className={styles.envDetailLabel}>Demo Public Key</span>
					<code className={styles.envDetailValue}>pk_test_demo</code>
				</div>
				<div className={styles.envDetail}>
					<span className={styles.envDetailLabel}>Demo Secret Key</span>
					<code className={styles.envDetailValue}>sk_test_demo</code>
				</div>
			</div>

			<Alert variant="warning" title="Demo Key Limitations">
				Demo keys are shared across all users and have stricter rate limits.
				Request your own sandbox credentials for serious development work.
			</Alert>

			<h3>Sandbox API Example</h3>

			<CodeBlock language="bash" title="Create a sandbox session">
				{`curl -X POST https://api.rngfort.com/api/v1/operators/sessions \\
  -H "Authorization: Bearer sk_test_your_sandbox_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "playerId": "test_player_001",
    "currency": "USD",
    "walletMode": "integrated"
  }'`}
			</CodeBlock>

			<h2>Live Environment</h2>

			<div className={`${styles.envCard} ${styles.live}`}>
				<div className={styles.envCardHeader}>
					<h3 className={styles.envCardTitle}>Live</h3>
					<Badge variant="live">Production</Badge>
				</div>
				<p className={styles.envCardDescription}>
					The live environment processes real transactions and connects to your
					production wallet system. Use this environment when you're ready to
					accept real bets from players.
				</p>
			</div>

			<h3>Key Characteristics</h3>

			<ul>
				<li>
					<strong>Real wallet integration</strong> - All transactions affect
					actual player balances
				</li>
				<li>
					<strong>Production security</strong> - Enhanced security measures and
					monitoring
				</li>
				<li>
					<strong>SLA guarantees</strong> - Uptime guarantees and support
					response time commitments
				</li>
				<li>
					<strong>Compliance logging</strong> - Full audit trail for regulatory
					compliance
				</li>
				<li>
					<strong>Permanent data retention</strong> - Transaction history stored
					according to regulatory requirements
				</li>
				<li>
					<strong>Production rate limits</strong> - Optimized limits for
					real-world usage patterns
				</li>
			</ul>

			<Alert variant="error" title="Production Data Warning">
				Live environment transactions are real and irreversible. Always test
				thoroughly in sandbox before deploying to production. Ensure your wallet
				integration is properly configured before accepting live bets.
			</Alert>

			<h3>Going Live Checklist</h3>

			<p>Before switching to the live environment, ensure you have:</p>

			<ol>
				<li>
					<strong>Completed sandbox testing</strong> - All game flows, error
					handling, and edge cases tested
				</li>
				<li>
					<strong>Configured wallet callbacks</strong> - Your wallet endpoints
					are production-ready (if using seamless mode)
				</li>
				<li>
					<strong>Set up webhook verification</strong> - Signature verification
					implemented and tested
				</li>
				<li>
					<strong>Reviewed security practices</strong> - API keys stored
					securely, HTTPS enforced
				</li>
				<li>
					<strong>Obtained live credentials</strong> - Requested and received
					live API keys from our team
				</li>
				<li>
					<strong>Configured monitoring</strong> - Error tracking and alerting
					in place
				</li>
			</ol>

			<CodeBlock language="bash" title="Create a live session">
				{`curl -X POST https://api.rngfort.com/api/v1/operators/sessions \\
  -H "Authorization: Bearer sk_live_your_production_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "playerId": "real_player_001",
    "currency": "USD",
    "walletMode": "seamless"
  }'`}
			</CodeBlock>

			<h2>Environment Comparison</h2>

			<Table columns={comparisonColumns} data={comparisonData} />

			<h2>Switching Between Environments</h2>

			<p>
				The environment is determined entirely by your API key. Both
				environments use the same API endpoints and response formats, making it
				easy to switch between them by simply changing your API key.
			</p>

			<CodeBlock language="typescript" title="Environment configuration">
				{`// Environment configuration
const config = {
  development: {
    apiKey: process.env.RINGFORT_SECRET_KEY_TEST,  // sk_test_*
    environment: 'sandbox',
  },
  production: {
    apiKey: process.env.RINGFORT_SECRET_KEY_LIVE,  // sk_live_*
    environment: 'live',
  },
};

// Get config based on NODE_ENV
const env = process.env.NODE_ENV === 'production' ? 'production' : 'development';
const { apiKey, environment } = config[env];

console.log(\`Using \${environment} environment\`);

// All API calls use the same base URL
const API_BASE = 'https://api.rngfort.com';

async function createSession(playerId: string, currency: string) {
  const response = await fetch(\`\${API_BASE}/api/v1/operators/sessions\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${apiKey}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId,
      currency,
      walletMode: 'integrated',
    }),
  });

  return response.json();
}`}
			</CodeBlock>

			<h3>Best Practices for Environment Management</h3>

			<ul>
				<li>
					<strong>Use environment variables</strong> - Never hardcode API keys
					in your codebase
				</li>
				<li>
					<strong>Separate configuration files</strong> - Keep sandbox and live
					configs in separate files or branches
				</li>
				<li>
					<strong>Automated deployment</strong> - Use CI/CD to ensure correct
					keys are deployed to each environment
				</li>
				<li>
					<strong>Key rotation</strong> - Rotate keys regularly, especially
					after team members leave
				</li>
			</ul>

			<CodeBlock language="bash" title="Example .env files">
				{`# .env.development
RINGFORT_PUBLIC_KEY=pk_test_abc123xyz
RINGFORT_SECRET_KEY=sk_test_def456uvw
RINGFORT_WEBHOOK_SECRET=whsec_test_789xyz

# .env.production (deployed separately)
RINGFORT_PUBLIC_KEY=pk_live_ghi789rst
RINGFORT_SECRET_KEY=sk_live_jkl012mno
RINGFORT_WEBHOOK_SECRET=whsec_live_345abc`}
			</CodeBlock>

			<h2>Data Isolation</h2>

			<p>
				Sandbox and live environments are completely isolated from each other:
			</p>

			<ul>
				<li>
					<strong>Separate databases</strong> - Player data, sessions, and
					transactions are stored separately
				</li>
				<li>
					<strong>No cross-environment access</strong> - Sandbox keys cannot
					access live data and vice versa
				</li>
				<li>
					<strong>Independent rate limits</strong> - Rate limit counters are
					tracked separately per environment
				</li>
				<li>
					<strong>Separate seed pools</strong> - Provably fair seeds are
					generated independently for each environment
				</li>
			</ul>

			<Alert variant="info" title="Player IDs Across Environments">
				The same <code>playerId</code> can exist in both environments as
				separate entities. A player in sandbox has no connection to the same
				player ID in live - they are treated as completely different players.
			</Alert>

			<h2>Rate Limits by Environment</h2>

			<Table
				columns={[
					{ key: 'endpoint', header: 'Endpoint' },
					{ key: 'sandbox', header: 'Sandbox Limit' },
					{ key: 'live', header: 'Live Limit' },
				]}
				data={[
					{
						endpoint: 'Session Creation',
						sandbox: '100 req/min',
						live: '1000 req/min',
					},
					{
						endpoint: 'Game API (per session)',
						sandbox: '200 req/min',
						live: '100 req/min',
					},
					{
						endpoint: 'Operator Dashboard',
						sandbox: '500 req/min',
						live: '500 req/min',
					},
					{
						endpoint: 'Bet Verification',
						sandbox: '500 req/min',
						live: '500 req/min',
					},
				]}
			/>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>

				<CardGrid columns={2}>
					<Card
						title="Authentication"
						description="Learn about API key formats and security best practices."
						href="/docs/authentication"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
								<path d="M7 11V7a5 5 0 0 1 10 0v4" />
							</svg>
						}
					/>

					<Card
						title="Integration Guide"
						description="Complete guide to integrating Ringfort games."
						href="/docs/integration-guide"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<polyline points="16 18 22 12 16 6" />
								<polyline points="8 6 2 12 8 18" />
							</svg>
						}
					/>

					<Card
						title="Testing Guide"
						description="Best practices for testing your integration."
						href="/docs/testing-guide"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<polyline points="9 11 12 14 22 4" />
								<path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
							</svg>
						}
					/>

					<Card
						title="Error Handling"
						description="Handle API errors gracefully in both environments."
						href="/docs/errors"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<circle cx="12" cy="12" r="10" />
								<line x1="12" y1="8" x2="12" y2="12" />
								<line x1="12" y1="16" x2="12.01" y2="16" />
							</svg>
						}
					/>
				</CardGrid>
			</div>
		</div>
	);
}
